import { forwardRef } from 'react'
import styled, { css } from 'styled-components'

import { CurrencyIcon } from '@root/entities/currency/ui'
import { PairLink } from '@root/entities/pair/ui/common'
import { useFormatDuration } from '@root/shared'
import { MarkerCapShortFormatted } from '@root/shared/core/format'
import { useTranslation } from '@root/shared/localization'

import type { PairCreatorStatsTopToken } from '@root/entities/pair/atoms/reatom-pair-creator-stats'

type Props = {
  place: 0 | 1 | 2
  devToken: PairCreatorStatsTopToken
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'>

export const DevToken = forwardRef<HTMLDivElement, Props>(
  ({ place, devToken, ...rest }, ref) => {
    const { t } = useTranslation()
    const { formatDuration } = useFormatDuration()

    return (
      <Root
        {...rest}
        ref={ref}
        $place={place}
        $withPointer={devToken.pairAddress !== undefined}
      >
        {devToken.pairAddress ? (
          <PairBackgroundLink
            pair={{
              address: devToken.pairAddress,
              network: devToken.currencyModel.network,
            }}
            onClick={(event) => {
              // The popover/tooltip content is portaled, but React still bubbles
              // clicks up the component tree to ancestor PairLinks in the nested
              // popover cascade — which would navigate to several pairs at once.
              event.stopPropagation()
            }}
          />
        ) : undefined}
        <Content $place={place}>
          <Main $place={place}>
            <Icon>
              <CurrencyIcon
                currency={devToken.currencyModel}
                size={39}
                withPreview={false}
                outline={{
                  type: 'custom',
                  colorString: iconOutlines[place],
                }}
              />
            </Icon>
            <Body>
              <Row>
                <Title>{devToken.currencyModel.ticker}</Title>
                <ATH>
                  {t('common.ath')}{' '}
                  <MarkerCapShortFormatted mcap={devToken.athMarketCap} />
                </ATH>
              </Row>
              <Row>
                <MCap>
                  {t('common.mcap-short')}{' '}
                  <MarkerCapShortFormatted mcap={devToken.currentMarketCap} />
                </MCap>
              </Row>
              <Row>
                <Time>
                  {t('explore.developer-tokens.24h-traders')}{' '}
                  {devToken.traders24h}
                </Time>
                <Time>
                  {formatDuration({
                    startAt: devToken.initTimestamp,
                    mode: 'singleUnitShort',
                  })}
                </Time>
              </Row>
            </Body>
          </Main>
        </Content>
      </Root>
    )
  },
)

DevToken.displayName = 'DevToken'

const Root = styled.div<{
  $place: 0 | 1 | 2
  $withPointer: boolean
}>`
  border-radius: 7px;
  padding: 1px;
  position: relative;
  ${({ $place }) => borders[$place]}
  ${({ $withPointer }) =>
    $withPointer &&
    css`
      cursor: pointer;
    `}

  &::before {
    content: '';
    display: ${({ $withPointer }) => ($withPointer ? 'block' : 'none')};

    position: absolute;
    inset: 0;
    border-radius: 7px;
    opacity: 0;
    transition: opacity 150ms ease;
    ${({ $place }) => borders[$place]}
  }

  &:hover::before {
    opacity: 1;
  }
`

const Content = styled.div<{ $place: 0 | 1 | 2 }>`
  border-radius: 6px;
  ${({ $place }) => backgrounds[$place]}
  pointer-events: none;
`
const Main = styled.div<{ $place: 0 | 1 | 2 }>`
  display: flex;
  gap: 8px;
  align-items: start;

  font-size: 10px;
  font-weight: 500;
  line-height: normal;
  padding: 7px 7px 6px;
  position: relative;
`

const firstBorder = css`
  background:
    radial-gradient(circle at 85% 0, var(--goldenA-6) 0%, transparent 35%),
    radial-gradient(circle at -15% -15%, var(--goldenA-6) 0%, transparent 35%);
`
const firstIconOutline = 'var(--golden-9)'

const firstBg = css`
  background: var(--background-3)
    radial-gradient(
      115.76% 473.98% at 125% -174.04%,
      var(--goldenA-2) 33%,
      var(--goldenA-2) 100%
    );
`
const secondBorder = css`
  background:
    radial-gradient(circle at 85% 0, var(--silver-6) 0%, transparent 35%),
    radial-gradient(circle at -15% -15%, var(--silver-6) 0%, transparent 35%);
`

const secondIconOutline = 'var(--silver-9)'

const secondBg = css`
  background: var(--background-3)
    radial-gradient(
      104.91% 436.58% at 135.68% -217.8%,
      var(--silverA-2) 33%,
      var(--silverA-2) 100%
    );
`
const thirdBorder = css`
  background:
    radial-gradient(circle at 85% 0, var(--bronze-6) 0%, transparent 35%),
    radial-gradient(circle at -15% -15%, var(--bronze-6) 0%, transparent 35%);
`

const thirdIconOutline = 'var(--bronze-9)'

const thirdBg = css`
  background: var(--background-3)
    radial-gradient(
      87.01% 275.07% at 107.22% -101.92%,
      var(--bronzeA-2) 33%,
      var(--bronzeA-2) 100%
    );
`

const backgrounds = [firstBg, secondBg, thirdBg] as const
const borders = [firstBorder, secondBorder, thirdBorder] as const
const iconOutlines = [
  firstIconOutline,
  secondIconOutline,
  thirdIconOutline,
] as const

const Icon = styled.div`
  pointer-events: auto;
`

const Body = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`
const Row = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`
const Title = styled.div`
  font-size: 11px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const MCap = styled.div`
  color: var(--text-12);
  white-space: nowrap;
`
const ATH = styled.div`
  color: var(--amber-10);
  text-align: right;
  white-space: nowrap;
`
const Time = styled.div`
  color: var(--text-9);
`

const PairBackgroundLink = styled(PairLink)`
  position: absolute;
  inset: 0;
`
